import React from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SearchField,
  Box,
  SimpleList,
  Paginator,
  Divider,
  CircularProgress,
  Button,
  Typography,
} from "@tiny/components";
import AudienceRow from "../../AudiencesListDialog/AudienceRow";
import { useMoveToAudienceDialog } from "./MoveToAudienceDialog.hooks";
import { MoveToAudienceDialogProps } from "./MoveToAudienceDialog.types";

function MoveToAudienceDialog(props: MoveToAudienceDialogProps) {
  const { open, onClose } = props;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      disableBackdropClose
    >
      <MoveToAudienceDialogContent {...props} />
    </Dialog>
  );
}

function MoveToAudienceDialogContent(props: MoveToAudienceDialogProps) {
  const { moveAll, selectedIds, totalCount: audienceTotal } = props;

  const {
    audiences,
    isLoading,
    isMoving,
    search,
    handleSearchChange,
    page,
    itemsPerPage,
    itemsPerPageOptions,
    totalCount,
    handlePageChange,
    handleSizeChange,
    targetId,
    handleSelect,
    handleConfirm,
    handleClose,
  } = useMoveToAudienceDialog(props);

  const count = moveAll ? audienceTotal : selectedIds.length;
  const noData = !isLoading && audiences.length === 0;

  return (
    <>
      <DialogTitle subtitle="Contacts will be removed from the current audience and added to the one you select.">
        {`Move ${count} ${
          count === 1 ? "contact" : "contacts"
        } to another audience`}
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box
            width="100%"
            height="300px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Box>
        ) : noData ? (
          <Box
            width="100%"
            height="300px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
          >
            <Typography
              variant="h4"
              color="grey"
              tone={800}
              mb={2}
              fontWeight="semiBold"
            >
              No audiences found
            </Typography>
            <Typography variant="body2" color="grey" tone={600}>
              There are no other uploaded audiences to move contacts into.
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={3} display="flex" alignItems="center">
              <SearchField
                value={search}
                placeholder="Search"
                onChangeValue={handleSearchChange}
                style={{ flex: 1 }}
              />
            </Box>
            <SimpleList>
              {audiences.map((row) => (
                <AudienceRow
                  key={row.id}
                  item={row}
                  closeDialog={handleClose}
                  mode="select"
                  selectMode
                  selected={targetId === row.id}
                  onClick={() => handleSelect(row.id)}
                />
              ))}
            </SimpleList>
            <Divider mb={2} />
            <Paginator
              mt={4}
              totalCount={totalCount}
              page={page}
              itemsPerPage={itemsPerPage}
              itemsPerPageOptions={itemsPerPageOptions}
              onItemsPerPageChange={handleSizeChange}
              onPageChange={handlePageChange}
              itemsPerPageLabel="results"
            />
          </>
        )}
      </DialogContent>

      {!noData && (
        <DialogActions>
          <Button onClick={handleClose} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={!targetId || isMoving}
          >
            Move
          </Button>
        </DialogActions>
      )}
    </>
  );
}

export default MoveToAudienceDialog;
